import React, { useState, useEffect, useRef } from "react";
import { Dialog } from '@headlessui/react';
import { FiX, FiMapPin, FiUser, FiMail, FiPhone, FiCalendar, FiUsers, FiBriefcase, FiClock, FiFileText, FiStar, FiTrendingUp, FiMessageSquare } from "react-icons/fi";
import { formatCurrency } from '../utils/format';
import { useMaps } from '../components/MapsProvider';
import LoadingSpinner from './LoadingSpinner';

// Format date for display
const formatDate = (dateStr) => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  } catch (e) {
    console.error("Date formatting error:", e);
    return dateStr;
  }
};

// Format price for display
const formatPrice = (price) => {
  try {
    return `€${parseFloat(price).toFixed(2)}`;
  } catch (e) {
    console.error("Price formatting error:", e);
    return `€${price}`;
  }
};

// Calculate approximate distance between two points
const calculateApproximateDistance = (lat1, lon1, lat2, lon2) => {
  try {
    // Validate inputs
    if (lat1 === null || lon1 === null || lat2 === null || lon2 === null || 
        isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) {
      return "Unknown";
    }
    
    // Haversine formula
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return distance < 1 
      ? `${Math.round(distance * 1000)} m` 
      : `${distance.toFixed(1)} km`;
  } catch (e) {
    console.error("Distance calculation error:", e);
    return "Unknown";
  }
};

// Status display component
const StatusBadge = ({ status }) => {
  const getStatusClass = () => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'bg-green-600/70 text-white';
      case 'pending': return 'bg-white/70 text-black';
      case 'cancelled': return 'bg-red-600/70 text-white';
      case 'completed': return 'bg-blue-600/70 text-white';
      default: return 'bg-gray-600/70 text-white';
    }
  };
  
  if (!status) return null;
  
  return (
    <span className={`mr-4 text-sm font-medium px-3 py-1 rounded-full ${getStatusClass()}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const ViewBookingModal = ({ booking, isOpen, onClose, onEdit, onDelete }) => {
  const { isLoaded, google, createMarkerIcon, mapOptions } = useMaps();
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const mapInstanceRef = useRef(null);
  
  // Clean up map elements
  const cleanupMap = () => {
    if (markersRef.current.length > 0) {
      markersRef.current.forEach(marker => {
        if (marker) marker.setMap(null);
      });
      markersRef.current = [];
    }
    
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
      directionsRendererRef.current = null;
    }
  };
  
  // Initialize map when modal opens or booking changes
  useEffect(() => {
    if (!isOpen || !isLoaded || !google?.maps || !mapContainerRef.current || !booking) {
      return;
    }
    
    // Clean up previous map elements
    cleanupMap();
    
    console.log("Initializing map for booking:", booking.id);
    setIsMapReady(false);
    
    try {
      // Safety check for Google Maps availability
      if (!window.google || !window.google.maps) {
        console.error("Google Maps not available in ViewBookingModal");
        return;
      }

      // Create map instance with error handling
      const map = new google.maps.Map(mapContainerRef.current, {
        ...mapOptions,
        zoom: 12,
        center: {
          lat: parseFloat(booking.pickup_lat || 0),
          lng: parseFloat(booking.pickup_lng || 0)
        }
      });
      
      mapInstanceRef.current = map;
      
      // Create pickup and dropoff positions
      const pickupPosition = {
        lat: parseFloat(booking.pickup_lat || 0),
        lng: parseFloat(booking.pickup_lng || 0)
      };
      
      const dropoffPosition = {
        lat: parseFloat(booking.dropoff_lat || 0),
        lng: parseFloat(booking.dropoff_lng || 0)
      };
      
      // Add pickup marker with error handling
      try {
        const pickupMarker = new google.maps.Marker({
          position: pickupPosition,
          map: map,
          title: booking.pickup_name || 'Pickup',
          icon: createMarkerIcon('pickup'),
          animation: google.maps.Animation ? google.maps.Animation.DROP : undefined
        });
        markersRef.current.push(pickupMarker);
      } catch (markerError) {
        console.warn("Error creating pickup marker:", markerError);
      }
      
      // Add dropoff marker with error handling
      try {
        const dropoffMarker = new google.maps.Marker({
          position: dropoffPosition,
          map: map,
          title: booking.dropoff_name || 'Dropoff',
          icon: createMarkerIcon('dropoff'),
          animation: google.maps.Animation ? google.maps.Animation.DROP : undefined
        });
        markersRef.current.push(dropoffMarker);
      } catch (markerError) {
        console.warn("Error creating dropoff marker:", markerError);
      }
      
      // Fit map bounds to include both markers with error handling
      try {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(pickupPosition);
        bounds.extend(dropoffPosition);
        map.fitBounds(bounds);
      } catch (boundsError) {
        console.warn("Error setting map bounds:", boundsError);
      }
      
      // Just calculate approximate distance for immediate display
      const approxDistance = calculateApproximateDistance(
        pickupPosition.lat, pickupPosition.lng,
        dropoffPosition.lat, dropoffPosition.lng
      );
      setDistance(approxDistance);
      setDuration("Calculating...");
      
      // We'll skip drawing the curved line initially and try to get the route first
      // Only if the route calculation fails, we'll show the fallback line
      calculateRoute(map, pickupPosition, dropoffPosition, true);
      
      setIsMapReady(true);
    } catch (error) {
      console.error("Error initializing map:", error);
      // Set fallback values
      const approxDistance = calculateApproximateDistance(
        parseFloat(booking.pickup_lat || 0), parseFloat(booking.pickup_lng || 0),
        parseFloat(booking.dropoff_lat || 0), parseFloat(booking.dropoff_lng || 0)
      );
      setDistance(approxDistance);
      setDuration("Route visualization unavailable");
    }
    
    // Cleanup function
    return () => {
      cleanupMap();
      setIsMapReady(false);
    };
  }, [isOpen, isLoaded, google, booking, createMarkerIcon, mapOptions]);
  
  // Helper function to draw a curved line
  const drawCurvedLine = (map, start, end) => {
    try {
      // Clean up existing polyline
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      
      // Create a curved path between markers
      const path = [];
      path.push(start);
      
      // Add curve points
      const numPoints = 8; // Increase number of points for smoother curve
      for (let i = 1; i < numPoints; i++) {
        const ratio = i / numPoints;
        const curveOffset = Math.sin(ratio * Math.PI) * 0.01; // Increase curve height slightly
        
        path.push({
          lat: start.lat + (end.lat - start.lat) * ratio + curveOffset,
          lng: start.lng + (end.lng - start.lng) * ratio + curveOffset
        });
      }
      
      path.push(end);
      
      // Create and display the line with improved styling
      const polyline = new google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: '#ffffff',
        strokeOpacity: 0.8,
        strokeWeight: 4,
        map: map,
        icons: [{
          icon: {
            path: 'M 0,-1 0,1',
            strokeOpacity: 1,
            scale: 3
          },
          offset: '0',
          repeat: '15px'
        }]
      });
      
      polylineRef.current = polyline;
      
      // Calculate approximate distance (straight line)
      const distance = calculateApproximateDistance(
        start.lat, start.lng, 
        end.lat, end.lng
      );
      
      setDistance(distance);
      setDuration("Estimated: 0:30");
      
      return polyline;
    } catch (error) {
      console.error("Error drawing curved line:", error);
      return null;
    }
  };
  
  // Calculate route using Google Directions API
  const calculateRoute = (map, origin, destination, showFallbackOnError = false) => {
    if (!google?.maps || !map) return;
    
    // Safety check for Google Maps availability
    if (!window.google || !window.google.maps || !window.google.maps.DirectionsService) {
      console.warn("Google Maps DirectionsService not available in ViewBookingModal");
      if (showFallbackOnError) {
        drawCurvedLine(map, origin, destination);
      }
      return;
    }
    
    // Add a loading indicator overlay during calculation
    const loadingDiv = document.createElement('div');
    loadingDiv.className = 'absolute inset-0 bg-black/60 flex items-center justify-center z-10';
    loadingDiv.innerHTML = `
      <div class="animate-spin h-10 w-10 border-4 border-white rounded-full border-t-transparent"></div>
    `;
    
    if (mapContainerRef.current) {
      mapContainerRef.current.appendChild(loadingDiv);
    }
    
    try {
      const directionsService = new google.maps.DirectionsService();
      
      directionsService.route({
        origin: origin,
        destination: destination,
        travelMode: google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false
      }, (response, status) => {
        // Remove loading indicator
        if (loadingDiv.parentNode) {
          loadingDiv.parentNode.removeChild(loadingDiv);
        }
        
        if (status === google.maps.DirectionsStatus.OK && response) {
          // Clean up existing renderer
          if (directionsRendererRef.current) {
            directionsRendererRef.current.setMap(null);
            directionsRendererRef.current = null;
          }
          
          // Remove the fallback polyline if it exists
          if (polylineRef.current) {
            polylineRef.current.setMap(null);
            polylineRef.current = null;
          }
          
          // Render the directions
          const renderer = new google.maps.DirectionsRenderer({
            map: map,
            directions: response,
            suppressMarkers: true, // We're using our own markers
            polylineOptions: {
              strokeColor: '#ffffff',
              strokeWeight: 4,
              strokeOpacity: 0.8
            },
            preserveViewport: false, // We want it to zoom to fit the route
            routeIndex: 0
          });
          
          directionsRendererRef.current = renderer;
          
          // Extract distance and duration
          if (response.routes && response.routes[0] && response.routes[0].legs && response.routes[0].legs[0]) {
            setDistance(response.routes[0].legs[0].distance.text);
            setDuration(response.routes[0].legs[0].duration.text);
          }
          
          console.log("Route calculated successfully");
        } else {
          console.warn("Failed to calculate route:", status);
          // If route calculation failed and we should show fallback, draw the curved line
          if (showFallbackOnError) {
            console.log("Using fallback curved line");
            drawCurvedLine(map, origin, destination);
          }
        }
      });
    } catch (error) {
      console.error("Error calculating route:", error);
      // Remove loading indicator
      if (loadingDiv.parentNode) {
        loadingDiv.parentNode.removeChild(loadingDiv);
      }
      
      // Show fallback on error if requested
      if (showFallbackOnError) {
        drawCurvedLine(map, origin, destination);
      }
    }
  };
  
  // Redraw Route button handler
  const handleRedrawRoute = () => {
    if (!mapInstanceRef.current || !booking || !google?.maps) return;
    
    // Safety check for Google Maps availability
    if (!window.google || !window.google.maps) {
      console.warn("Google Maps not available for route redraw");
      return;
    }
    
    try {
      const map = mapInstanceRef.current;
      
      const pickupPosition = {
        lat: parseFloat(booking.pickup_lat || 0),
        lng: parseFloat(booking.pickup_lng || 0)
      };
      
      const dropoffPosition = {
        lat: parseFloat(booking.dropoff_lat || 0),
        lng: parseFloat(booking.dropoff_lng || 0)
      };
      
      // Clean up existing renderer and polyline
      cleanupMap();
      
      // Re-add markers since cleanupMap removes them with error handling
      try {
        const pickupMarker = new google.maps.Marker({
          position: pickupPosition,
          map: map,
          title: booking.pickup_name || 'Pickup',
          icon: createMarkerIcon('pickup')
        });
        
        const dropoffMarker = new google.maps.Marker({
          position: dropoffPosition,
          map: map,
          title: booking.dropoff_name || 'Dropoff',
          icon: createMarkerIcon('dropoff')
        });
        
        markersRef.current = [pickupMarker, dropoffMarker];
      } catch (markerError) {
        console.warn("Error creating markers during redraw:", markerError);
      }
      
      // Reset bounds with error handling
      try {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(pickupPosition);
        bounds.extend(dropoffPosition);
        map.fitBounds(bounds);
      } catch (boundsError) {
        console.warn("Error setting bounds during redraw:", boundsError);
      }
      
      // Update status text while recalculating
      setDistance("Recalculating...");
      setDuration("Recalculating...");
      
      // Try to calculate the route directly, fallback to curved line only if it fails
      calculateRoute(map, pickupPosition, dropoffPosition, true);
    } catch (error) {
      console.error("Error redrawing route:", error);
    }
  };
  
  if (!booking) return null;

  // Format booking date
  const bookingDate = new Date(booking.datetime);
  const formattedDate = bookingDate.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Europe/Athens'
  });
  
  const formattedTime = bookingDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Athens'
  });

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-80" />
        
        <div className="bg-[#0a0a0a] relative rounded-xl shadow-2xl max-w-5xl w-full mx-4 my-8 max-h-[90vh] overflow-hidden border border-white/20">
          <div className="px-6 py-4 border-b border-white/20 flex justify-between items-center bg-gradient-to-r from-[#111] to-[#111] shadow-md">
            <Dialog.Title className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-yellow-600">
              Booking Details
            </Dialog.Title>
            
            <div className="flex items-center">
              <StatusBadge status={booking.status} />
              
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="overflow-y-auto max-h-[calc(90vh-4rem)]">
            {/* Map section - at the top for prominence */}
            <div className="relative w-full h-[500px] bg-black/20">
              {!isLoaded ? (
                <div className="flex flex-col items-center justify-center h-full bg-black/40">
                  <LoadingSpinner size="lg" />
                  <p className="text-white mt-4">Loading Google Maps API...</p>
                </div>
              ) : (
                <div className="h-full w-full relative">
                  <div 
                    ref={mapContainerRef} 
                    className="h-full w-full"
                  />
                  
                  {/* Redraw button */}
                  <button
                    onClick={handleRedrawRoute}
                    className="absolute bottom-2 right-2 bg-black/60 border border-white/30 text-white text-xs px-2 py-1 rounded-md hover:bg-white/30 transition-colors z-50"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 inline-block mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    Redraw Route
                  </button>
                </div>
              )}
            </div>
            
            {/* Distance and time indicator */}
            {isMapReady && (
              <div className="text-center py-2 text-xs text-green-500">
                Route: {distance || '...'} • {duration || '...'}
              </div>
            )}
            
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left column - Customer & Trip Details */}
              <div className="space-y-6">
                {/* Customer Information */}
                <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <h3 className="text-lg font-bold text-white pb-2 border-b border-white/20 mb-4">
                    Customer Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <FiUser className="text-gray-400 mt-1 mr-3" />
                      <div>
                        <span className="block text-sm text-gray-300">Full Name</span>
                        <span className="block text-white font-medium">{booking.full_name}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiMail className="text-gray-400 mt-1 mr-3" />
                      <div>
                        <span className="block text-sm text-gray-300">Email</span>
                        <span className="block text-white font-medium">{booking.email}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiPhone className="text-gray-400 mt-1 mr-3" />
                      <div>
                        <span className="block text-sm text-gray-300">Phone</span>
                        <span className="block text-white font-medium">{booking.phone}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Special Requests / Comments */}
                {booking.comments && booking.comments.trim() && (
                  <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                    <h3 className="text-lg font-bold text-white pb-2 border-b border-white/20 mb-4">
                      💬 Special Requests / Comments
                    </h3>
                    
                    <div className="flex items-start">
                      <FiMessageSquare className="text-yellow-400 mt-1 mr-3" />
                      <div className="flex-1">
                        <span className="block text-sm text-gray-300 mb-2">Customer requests</span>
                        <div className="bg-white/10 rounded-lg p-3 border border-white/20">
                          <span className="block text-white font-medium">{booking.comments.trim()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Trip Details */}
                <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <h3 className="text-lg font-bold text-white pb-2 border-b border-white/20 mb-4">
                    Trip Details
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <FiCalendar className="text-gray-400 mt-1 mr-3" />
                      <div>
                        <span className="block text-sm text-gray-300">Date & Time</span>
                        <span className="block text-white font-medium">{formattedDate}</span>
                        <span className="block text-sm text-white">{formattedTime}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiUsers className="text-gray-400 mt-1 mr-3" />
                      <div>
                        <span className="block text-sm text-gray-300">Passengers</span>
                        <span className="block text-white font-medium">{booking.passengers}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiBriefcase className="text-gray-400 mt-1 mr-3" />
                      <div>
                        <span className="block text-sm text-gray-300">Luggage Items</span>
                        <span className="block text-white font-medium">{booking.luggage || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right column - Location & Payment Details */}
              <div className="space-y-6">
                {/* Location Details */}
                <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <h3 className="text-lg font-bold text-white pb-2 border-b border-white/20 mb-4">
                    Location Details
                  </h3>
                  
                  <div className="space-y-5">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-900/30 flex items-center justify-center mt-1 mr-3">
                        <FiMapPin className="text-gray-400" />
                        <span className="absolute w-1 h-5 bg-white/50 top-8 left-4"></span>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-300">Pickup Location</span>
                        <span className="block text-white font-medium">{booking.pickup_name}</span>
                        <span className="block text-xs text-gray-400 mt-1">{booking.pickup_lat?.toFixed(6)}, {booking.pickup_lng?.toFixed(6)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-900/30 flex items-center justify-center mt-1 mr-3">
                        <FiMapPin className="text-green-500" />
                      </div>
                      <div>
                        <span className="block text-sm text-gray-300">Dropoff Location</span>
                        <span className="block text-white font-medium">{booking.dropoff_name}</span>
                        <span className="block text-xs text-gray-400 mt-1">{booking.dropoff_lat?.toFixed(6)}, {booking.dropoff_lng?.toFixed(6)}</span>
                      </div>
                    </div>
                    
                    {distance && duration && (
                      <div className="mt-6 grid grid-cols-2 gap-4 py-3 px-4 bg-white/10 rounded-lg border border-white/20">
                        <div className="flex items-center">
                          <FiTrendingUp className="text-gray-400 mr-2" />
                          <div>
                            <span className="block text-xs text-gray-300">Distance</span>
                            <span className="block text-white font-medium">
                              {distance}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <FiClock className="text-gray-400 mr-2" />
                          <div>
                            <span className="block text-xs text-gray-300">Duration</span>
                            <span className="block text-white font-medium">
                              {duration}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Payment Details */}
                <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <h3 className="text-lg font-bold text-white pb-2 border-b border-white/20 mb-4">
                    Payment Details
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-300">Total Price</span>
                      <span className="text-xl font-bold text-gray-400">{formatCurrency(booking.price)}</span>
                    </div>
                    
                    {booking.price_manually_set && (
                      <div className="bg-gray-900/10 text-white text-sm rounded-lg p-2 text-center">
                        <FiStar className="inline-block mr-1" />
                        Custom price set manually
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-white/20 flex justify-end space-x-4">
              <button
                onClick={() => {
                  // Create Google Maps URL with the route parameters
                  const origin = booking.pickup_lat && booking.pickup_lng ? 
                    `${parseFloat(booking.pickup_lat).toFixed(6)},${parseFloat(booking.pickup_lng).toFixed(6)}` : '';
                  const destination = booking.dropoff_lat && booking.dropoff_lng ? 
                    `${parseFloat(booking.dropoff_lat).toFixed(6)},${parseFloat(booking.dropoff_lng).toFixed(6)}` : '';
                  const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
                  // Open in a new tab
                  window.open(url, '_blank');
                }}
                className="px-5 py-3 flex items-center bg-black hover:bg-gray-900/20 text-gray-300 rounded-lg border border-white700/30 transition-colors"
              >
                <FiMapPin className="mr-2" />
                View in Google Maps
              </button>
              <button
                onClick={() => onEdit(booking)}
                className="px-5 py-3 bg-white hover:bg-gray-200 text-black rounded-lg transition-colors shadow-lg font-medium"
              >
                Edit Booking
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ViewBookingModal; 